import type React from "react";
import { useRef, useCallback } from "react";

import { useField } from "formik";
import { Button } from "reactstrap";
import { ArrowUpTrayIcon } from "@heroicons/react/24/outline";

import { ImageThumbnail } from "./ImageThumbnail";
import { useUploader } from "./hooks/useUploader";

interface InlineUploaderProps {
  name: string;
  accept: string;
  disabled?: boolean;
  invalid?: boolean;
  existingImageUrl?: string;
}

/**
 * InlineUploader is a compact single file upload component with a simplified inline layout.
 *
 * Use this component when you need:
 * - Single file upload in a compact, inline format
 * - Simple preview with replace functionality
 * - Integration with Formik forms
 *
 * This component is ideal for:
 * - Profile pictures
 * - Logo uploads
 * - Any other single-image upload that needs to be compact
 *
 * @example
 * <InlineUploader
 *   name="logo"
 *   accept="image/*"
 * />
 *
 * @param {Object} props
 * @param {string} props.name - Formik field name
 * @param {string} props.accept - File types to accept (e.g. "image/*")
 * @param {boolean} [props.disabled=false] - Disable the uploader
 * @param {boolean} [props.invalid=false] - Mark the uploader as invalid
 */
export const InlineUploader: React.FC<InlineUploaderProps> = ({
  name,
  accept,
  disabled = false,
  invalid = false,
  existingImageUrl,
  ...props
}) => {
  const [field, , { setValue, setTouched }] = useField(name);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = useCallback(
    (file: File | File[] | null) => {
      setValue(file);
      setTouched(true);
    },
    [setValue, setTouched],
  );

  const {
    handleFileSelect: onFileSelect,
    handleDelete,
    getPreviewUrl,
  } = useUploader({
    onFileSelect: handleFileSelect,
  });

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onFileSelect(event.currentTarget.files);
    },
    [onFileSelect],
  );

  const fileValue = field.value as File;
  const acceptDescription = accept.replace("image/*", "SVG, PNG, JPG or GIF");

  // Get the preview URL - file takes precedence over existing URL
  let previewUrl: string | null;
  if (fileValue) {
    previewUrl = getPreviewUrl(fileValue, 0);
  } else if (fileValue === null) {
    previewUrl = null;
  } else {
    previewUrl = existingImageUrl || null;
  }

  return (
    <div className="inline-uploader d-flex w-100 gap-2">
      <input
        ref={fileInputRef}
        type="file"
        multiple={false}
        accept={accept}
        onChange={handleChange}
        onBlur={field.onBlur}
        name={field.name}
        id={field.name}
        disabled={disabled}
        className="visually-hidden"
        aria-invalid={invalid}
        {...props}
      />

      <ImageThumbnail
        previewUrl={previewUrl}
        altText={fileValue?.name || "existing image"}
        onDelete={handleDelete}
        disabled={disabled}
        showDeleteButton={true}
      />

      <div className="upload-controls d-flex flex-column align-items-start gap-1">
        <Button
          color="secondary"
          outline
          className={`upload-controls-button d-flex align-items-center justify-content-center gap-1 ${invalid ? "is-invalid" : ""}`}
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled}
        >
          <ArrowUpTrayIcon height={20} width={20} />
          {previewUrl ? "Replace" : "Upload"}
        </Button>
        {(fileValue?.name || acceptDescription) && (
          <p className="text-muted small m-0">
            {fileValue?.name || acceptDescription}
          </p>
        )}
      </div>
    </div>
  );
};
